import { Link } from "react-router-dom";
import { Target, CheckSquare, FileText, ArrowRight, CheckCircle, DollarSign, Scale, Download } from "lucide-react";

const AccountabilityPage = () => {
  const keyPoints = [
    "Sign Personal Accountability Contracts for each major bill I promise to pass",
    "$5,000,000 personal penalty for each bill I fail to sign — my money, not taxpayer money",
    "Drafted legislation ready BEFORE taking office",
    "Contracts protect voters from lobbyists, PACs, and large donor pressure",
    "Exception only if lawmakers add unrelated provisions that change the bill",
    "Calling on every politician to sign Accountability Contracts for every promise",
  ];

  const howItWorks = [
    { step: "1", title: "Draft Legislation", description: "Before taking office, I draft the exact bills Nevadans want — lowering costs, expanding healthcare, powering Nevada" },
    { step: "2", title: "Sign Contract", description: "I sign a Personal Accountability Contract for each bill, with a $5 million penalty if I don't deliver" },
    { step: "3", title: "Bill Reaches Desk", description: "When the bill reaches my desk the way I promised it would — I sign it into law" },
    { step: "4", title: "Accountability", description: "If I break my word, I pay $5 million per bill to an independent Nevada Accountability Fund" },
  ];

  return (
    <div data-testid="accountability-page" className="pt-16 md:pt-20">
      {/* Hero */}
      <section className="relative py-16 md:py-24 bg-slate-900">
        <div className="absolute inset-0 bg-[#002B7F]/90" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            Government Reform
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-6">
            Real Accountability
          </h1>
          <p className="text-lg text-white/80 max-w-2xl">
            For too long, politicians have said whatever it takes to get elected — then broken every promise once in office. That ends with me.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                The Problem
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                For too long, politicians have treated elections like a game. They say whatever will get them elected... then once they're in office, nothing changes. Promises are forgotten. Programs never start. And voters are left frustrated, angry, and unheard.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Why does this happen? Because of pressure from <strong>lobbyists, political action committees (PACs), and large donors</strong>. These groups spend millions of dollars to influence politicians. And too often, that influence means politicians end up working for them — instead of working for you.
              </p>

              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                The Solution: Personal Accountability Contracts
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                <strong>That ends with me.</strong> Before taking office, I am drafting the exact legislation that Nevadans want to see — bills that lower our cost of living, expand access to healthcare, power Nevada homes and businesses, and more.
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                Then, I will sign a <strong>Personal Accountability Contract</strong> for each one.
              </p>

              {/* How It Works */}
              <div className="bg-[#F8F9FA] p-6 border border-slate-200 mb-8">
                <h3 className="text-lg font-bold text-slate-900 mb-4">
                  How It Works
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {howItWorks.map((item, index) => (
                    <div key={index} className="p-4 bg-white border border-slate-200">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white font-bold">
                          {item.step}
                        </div>
                        <div>
                          <p className="font-bold text-slate-900">{item.title}</p>
                          <p className="text-sm text-slate-600">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* The Penalty */}
              <div className="bg-[#002B7F] p-6 text-white mb-8">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                  The Penalty: $5,000,000 Per Bill
                </h3>
                <p className="text-white/90 leading-relaxed mb-4">
                  If one of the bills I had drafted reaches my desk, I will sign it into law. If I don't — I will pay a personal financial penalty of <strong>$5,000,000</strong> for each bill I fail to sign, paid directly to an independent Nevada Accountability Fund.
                </p>
                <p className="text-white/90 leading-relaxed mb-4">
                  This is <strong>my personal money — not taxpayer money</strong>.
                </p>
                <p className="text-[#D4AF37] font-bold">
                  And I want to be very clear: I don't have $5,000,000. Which means if I break my word, I will be paying that off for the rest of my life. That's how serious I am about doing what I say.
                </p>
              </div>

              {/* Exception */}
              <div className="bg-amber-50 border border-amber-200 p-6 mb-8">
                <h3 className="font-bold text-amber-800 mb-3">
                  The Only Exception
                </h3>
                <p className="text-amber-900 leading-relaxed">
                  Sometimes lawmakers add unrelated provisions into bills — things that have nothing to do with the original purpose. If that happens, and the bill is no longer what I originally wrote, then I am not penalized. But if the bill comes to my desk the way I promised it would — <strong>there are no excuses</strong>.
                </p>
              </div>

              {/* Why This Protects You */}
              <div className="bg-[#F8F9FA] p-6 border border-slate-200 mb-8">
                <h3 className="text-lg font-bold text-slate-900 mb-4">
                  Why This Protects You
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  This contract makes it <strong>impossible for me to cave to pressure from lobbyists, PACs, or donors</strong> — because if I do, it will cost me millions of dollars personally. This isn't about politics. It's about trust. It's about making sure that when a leader makes a promise, they are willing to stand behind it — not just with words, but with real consequences.
                </p>
              </div>

              {/* Call to All Politicians */}
              <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-6 mb-8">
                <h3 className="text-lg font-bold text-[#002B7F] mb-3">
                  A Challenge to Every Politician
                </h3>
                <p className="text-slate-700 leading-relaxed mb-4">
                  I understand that $5 million sounds gimmicky. But it's because I want every politician to do the same and sign Accountability Contracts for every promise you make to your voters.
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    Stop making promises you don't intend to keep.
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    Stop saying whatever it takes to win an election.
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    Stop answering to donors instead of the people you represent.
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    Serve your constituents. Be honest. Be accountable.
                  </li>
                </ul>
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Key Points
              </h2>
              <ul className="space-y-4 mb-8">
                {keyPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{point}</span>
                  </li>
                ))}
              </ul>

              {/* Vision */}
              <div className="bg-[#002B7F] p-6 text-white">
                <p className="leading-relaxed mb-4">
                  Imagine a government where leaders are judged not by what they say — but by what they actually do. Imagine a Nevada where promises aren't just campaign slogans — they're commitments backed by real accountability.
                </p>
                <p className="leading-relaxed font-semibold text-[#D4AF37]">
                  That's the Nevada I want to build. This is how we rebuild trust in government. This is how we make Nevada work for everyone — not just for politicians and special interests.
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Why This Matters */}
              <div className="bg-[#002B7F] p-6 text-white mb-8">
                <h3 className="text-lg font-bold mb-4">Why This Matters to You</h3>
                <p className="text-white/90 text-sm leading-relaxed mb-4">
                  You've been lied to by politicians before. You've watched them promise the world during campaigns and deliver nothing in office.
                </p>
                <p className="text-white/90 text-sm leading-relaxed">
                  This contract changes everything. It means when I make a promise, <strong>I'm putting my money where my mouth is</strong> — literally.
                </p>
              </div>

              {/* The Contract */}
              <div className="bg-[#F8F9FA] p-6 border border-slate-200 mb-8">
                <h3 className="text-lg font-bold text-slate-900 mb-4">The Contract</h3>
                <div className="space-y-4">
                  <div className="border-b border-slate-200 pb-4">
                    <p className="text-3xl font-black text-[#D4AF37]">$5M</p>
                    <p className="text-sm text-slate-600">Personal penalty per broken promise</p>
                  </div>
                  <div className="border-b border-slate-200 pb-4">
                    <p className="text-3xl font-black text-[#002B7F]">$0</p>
                    <p className="text-sm text-slate-600">Taxpayer money at risk</p>
                  </div>
                  <div>
                    <p className="text-3xl font-black text-[#002B7F]">100%</p>
                    <p className="text-sm text-slate-600">Accountability to Nevada voters</p>
                  </div>
                </div>
              </div>

              {/* Download Contract */}
              <a
                href="https://customer-assets.emergentagent.com/job_solutions-nv/artifacts/4xcyqms5_Personal%20Accountability%20Contract%20%281%29.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-accountability-contract"
                className="flex items-center justify-center gap-2 w-full py-4 bg-[#002B7F] hover:bg-[#001F5C] text-white text-center font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl mb-4"
              >
                <Download className="w-4 h-4" />
                Read the Contract
              </a>

              <Link
                to="/volunteer"
                className="block w-full py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black text-center font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                Get Involved
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AccountabilityPage;
